import { Component, Input, SimpleChanges } from '@angular/core';
import * as Highcharts from 'highcharts';
import { reportChartLineOptions } from '../highcharts-options';
import {
  PositionStatus,
  QuickBuild,
  QuickBuildService,
  UIFilter,
} from 'src/app/people/quick-build.service';
import { PeopleReportService } from 'src/app/people/report-service.service';
import { departments, levels, ReportVizChartColors } from '../constants';
import * as moment from 'moment';
import { getPeopleColumnByName } from 'src/app/people/people-columns';
import { BaseReportComponent, ChartContextMenuItem } from '../base-report.component';
import { Log } from 'src/app/logger.service';
import { SearchFilter } from 'ldt-people-api';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import * as dayjs from 'dayjs';
import { JobStartsAndEndsConfig } from '../interfaces/report-config.interfaces';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

const FIELDS_TO_GROUP_BY = ['jobs.function', 'jobs.level'];
const REPORT_NAME = 'arrivals_departures';
@Component({
  selector: 'app-reports-job-starts-and-ends',
  templateUrl: './job-starts-and-ends.component.html',
  styleUrls: ['./job-starts-and-ends.component.scss'],
  standalone: false,
})
export class JobStartsAndEndsComponent extends BaseReportComponent<JobStartsAndEndsConfig> {
  @Input() dateFrom: string;
  @Input() dateTo: string;
  @Input() field: string;
  @Input() maxSeries: number = 25;
  @Input() allowFieldSelection = true;
  @Input() showAIAnalysis = false;
  @Input() boxShadow: boolean = true;

  @Input()
  set selectedBreakdownBy(value: string | undefined) {
    if (value) {
      this._selectedBreakdownBy = value;
    }
  }
  get selectedBreakdownBy(): string {
    return this._selectedBreakdownBy;
  }

  @Input()
  set selectedSeries(value: 'arrivals' | 'departures' | 'both' | undefined) {
    if (value) {
      this._selectedSeries = value;
    }
  }
  get selectedSeries(): 'arrivals' | 'departures' | 'both' {
    return this._selectedSeries;
  }

  breakdownByOptions = FIELDS_TO_GROUP_BY;
  private _selectedBreakdownBy: (typeof FIELDS_TO_GROUP_BY)[number] = FIELDS_TO_GROUP_BY[0];
  private _selectedSeries: 'arrivals' | 'departures' | 'both' = 'both';

  seriesOptions = [
    { value: 'both', displayText: 'Both' },
    { value: 'arrivals', displayText: 'Arrivals' },
    { value: 'departures', displayText: 'Departures' },
  ];

  breakdownByToggleOptions = this.breakdownByOptions.map((option) => ({
    value: option,
    displayText: getPeopleColumnByName(option).displayName,
  }));

  protected readonly CHART_ID = 'arrivals_departures';
  protected readonly CHART_DATA_DESC =
    'Arrival and departure counts by month broken out by job function or level';
  protected readonly EMP_COUNT_CHART_ID = 'employee_count_by_month';
  protected readonly EMP_COUNT_CHART_DATA_DESC = 'Total active employee count by month';

  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsDrawerService: StreamsSubscribeDrawerService,
    auth: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    Log.d('JobStartsAndEndsComponent constructor');
    super(
      reportService,
      quickBuildService,
      streamsDrawerService,
      auth,
      statsigService,
      layoutService
    );
    if (!this.dateFrom || !this.dateTo) {
      this.dateFrom = moment().subtract(2, 'year').format('YYYY-MM');
      this.dateTo = moment().format('YYYY-MM');
    }

    // Register configuration properties for automatic emission
    this.registerConfigProperty('selectedBreakdownBy', () => this._selectedBreakdownBy);
    this.registerConfigProperty('selectedSeries', () => this._selectedSeries);
  }

  protected initializeChart(): void {
    this.chart = Highcharts.chart(
      `highchartsContainer-${this.chartId}`,
      Highcharts.merge(reportChartLineOptions)
    );
    this.chart.update(jobStartsAndEndsChartNoSeries, true, true);
    this.initializeContextMenu();
  }

  protected shouldRefreshOnChanges(changes: SimpleChanges): boolean {
    if ('field' in changes) {
      this._selectedBreakdownBy = this.field;
      return true;
    }
    return super.shouldRefreshOnChanges(changes);
  }

  protected getChartData(): void {
    Log.d('getChartData', this.quickBuild);
    if (!this.quickBuild) return;

    this.showLoading();

    const reportParams = {
      date_from: this.dateFrom,
      date_to: this.dateTo,
      group_by: [this._selectedBreakdownBy],
    };

    const totalCountParams = {
      date_from: this.dateFrom,
      date_to: this.dateTo,
      aggregate_type: 'month',
    };
    const sub2 = this.reportService.getReportDataWithReportDetails(
      this.reportId,
      'demographics',
      totalCountParams,
      this.reportTarget,
      this.quickBuild
    );

    const sub = this.reportService
      .getReportDataWithReportDetails(
        this.reportId,
        REPORT_NAME,
        reportParams,
        this.reportTarget,
        this.quickBuild
      )
      .subscribe({
        next: (data) => {
          Log.d('JobStartsAndEndsComponent data', data);
          const series = this.generateChartSeries(
            data.data.arrivals_departures,
            this._selectedBreakdownBy
          );
          let titleText = undefined;
          if (this._selectedBreakdownBy) {
            const titlePrefix =
              this._selectedSeries === 'both'
                ? 'Job Starts and Ends'
                : this._selectedSeries === 'arrivals'
                  ? 'Job Starts'
                  : 'Job Ends';
            titleText = titlePrefix + ' by ' + this.getFieldDisplayName(this._selectedBreakdownBy);
          }
          this.updateChart(series, undefined, titleText);

          const sub2sub = sub2.subscribe((data) => {
            Log.d('JobStartsAndEndsComponent data2', data);
            const lineSeries = this.generateTotalEmpCountByMonthChartSeries(data.data.demographics);
            this.addSeriesToChart(lineSeries);
          });
          this.subscriptions.add(sub2sub);
          this.chartLoadingComplete();
        },
        error: (error) => {
          Log.e('JobStartsAndEndsComponent getChartData error', error);
          this.showChartError(error);
        },
      });
    this.subscriptions.add(sub);
  }

  protected generateTotalEmpCountByMonthChartSeries(inputData: any): any {
    return {
      name: 'Employee Count',
      data: inputData.map((d: any) => [new Date(d.date!).valueOf(), d.count_employees]),
      type: 'spline',
      marker: {
        enabled: false,
      },
      color: '#3F83F8',
      yAxis: 1,
      showInLegend: false,
      zIndex: 99,
    };
  }

  protected generateChartSeries(inputData: any, byType: string): any[] {
    const totalKeyCounts: Record<string, number> = {};
    let processed = inputData.reduce((acc: any, d: any) => {
      const keyVal = d['group_values'][0]?.value || 'Total';
      if (!acc[keyVal]) {
        acc[keyVal] = {};
        acc[keyVal + 'DEP'] = {};
      }

      if (!totalKeyCounts[keyVal]) {
        totalKeyCounts[keyVal] = 0;
      }
      totalKeyCounts[keyVal] += d.arrivals + d.departures;

      const dateVal = d.date;

      acc[keyVal][dateVal] = d.arrivals;
      acc[keyVal + 'DEP'][dateVal] = -1 * d.departures;

      return acc;
    }, {});

    const indexer =
      byType === 'jobs.function'
        ? (val: string) => departments.indexOf(val)
        : (val: string) => levels.indexOf(val);

    const topKeys = Object.entries(totalKeyCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, this.maxSeries)
      .map(([key, _]) => key);

    return Object.entries(processed)
      .filter(([key, _]) => topKeys.includes(this.rootKeyName(key)))
      .filter(([key, _]) => {
        if (this._selectedSeries === 'arrivals') {
          return !key.endsWith('DEP');
        } else if (this._selectedSeries === 'departures') {
          return key.endsWith('DEP');
        }
        return true;
      })
      .map(([key, value]) => {
        const keyName = this.rootKeyName(key);

        return {
          id: key.endsWith('DEP') ? undefined : key,
          linkedTo:
            key.endsWith('DEP') && this._selectedSeries !== 'departures' ? ':previous' : undefined,
          name: keyName,
          data: Object.entries(value as Record<string, number>).map(([date, count]) => [
            new Date(date!).valueOf(),
            count,
          ]),
          color: ReportVizChartColors[indexer(keyName)],
          type: 'column',
          zIndex: 100,
        };
      });
  }

  private rootKeyName(key: string): string {
    return key.endsWith('DEP') ? key.replace('DEP', '') : key;
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    const date = dayjs(point.x as number);
    const value = point.y as number;
    const series = point.series;
    const category = point.category as string;
    const breakdownBy = this._selectedBreakdownBy;

    Log.d('job-starts-and-ends-component getContextMenuItems', point);

    const qbCopy = QuickBuildService.copyQuickBuild(this.quickBuild);

    // For the employee count series, we just have one clickable option and it's pretty simple
    if (series.name === 'Employee Count') {
      return this.getContextMenuItemsForEmployeeCount(qbCopy, date);
    }

    const categoryFilter: UIFilter = {
      filter: {
        field: breakdownBy,
        match_type: SearchFilter.MatchTypeEnum.Exact,
        string_values: [series.name],
        type: SearchFilter.TypeEnum.Must,
      },
    };
    const startsDateFilter: UIFilter = {
      filter: {
        field: 'jobs.started_at',
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        date_from: date.toISOString(),
        date_to: date.toISOString(),
      },
    };
    const endsDateFilter: UIFilter = {
      filter: {
        field: 'jobs.ended_at',
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        date_from: date.toISOString(),
        date_to: date.toISOString(),
      },
    };

    const movementType = value > 0 ? 'arrivals' : 'departures';

    const dateFilter = movementType === 'arrivals' ? startsDateFilter : endsDateFilter;
    const positionStatus = movementType === 'arrivals' ? PositionStatus.First : PositionStatus.Last;

    Log.d('job-starts-and-ends-component getContextMenuItems', categoryFilter, dateFilter);

    return [
      {
        label: `View ${movementType} for ${series.name} in ${date.utc().format('MMM YYYY')}`,
        icon: 'calendar_month',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(
            qbCopy,
            [categoryFilter, dateFilter],
            'moneyball'
          );
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.positionStatus = positionStatus;
          qbCopy.name = `People ${movementType} for ${series.name} in ${date.utc().format('MMM YYYY')}`;
          this.openDrawer(qbCopy);
        },
      },
      {
        label: `View ${movementType} for ${series.name} all time`,
        icon: 'group',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [categoryFilter], 'moneyball');
          qbCopy.name = `People ${movementType} for ${series.name} all time`;
          this.openDrawer(qbCopy);
        },
      },
      {
        label: `View all ${movementType} in ${date.utc().format('MMM YYYY')}`,
        icon: 'bar_chart',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [dateFilter], 'moneyball');
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          group!.positionStatus = positionStatus;
          qbCopy.name = `All ${movementType} in ${date.utc().format('MMM YYYY')}`;
          this.openDrawer(qbCopy);
        },
      },
    ];
  }

  private getContextMenuItemsForEmployeeCount(
    qbCopy: QuickBuild,
    date: dayjs.Dayjs
  ): ChartContextMenuItem[] {
    const activeAtFilter: UIFilter = {
      filter: {
        field: 'job_active_at',
        match_type: SearchFilter.MatchTypeEnum.Fuzzy,
        type: SearchFilter.TypeEnum.Must,
        date_from: date.toISOString(),
      },
    };

    return [
      {
        label: `View all employees active in ${date.utc().format('MMM YYYY')}`,
        icon: 'bar_chart',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [activeAtFilter], 'moneyball');
          const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
          group!.isJobsGroup = true;
          qbCopy.name = `${this.quickBuild.name} employees active in ${date.utc().format('MMM YYYY')}`;
          this.openDrawer(qbCopy);
        },
      },
    ];
  }

  onBreakdownChanged(value: string) {
    this._selectedBreakdownBy = value as (typeof FIELDS_TO_GROUP_BY)[number];
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }

  onSeriesChanged(value: string) {
    Log.d('JobStartsAndEndsComponent onSeriesChanged', value);
    this._selectedSeries = value as 'arrivals' | 'departures' | 'both';
    Log.d('JobStartsAndEndsComponent onSeriesChanged', this._selectedSeries);
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }
}

const jobStartsAndEndsChartNoSeries: Highcharts.Options = {
  title: {
    text: 'Job Starts and Ends',
  },
  yAxis: [
    {
      allowDecimals: false,
      tickAmount: 10,
      title: {
        text: 'Ends | Starts',
      },
      stackLabels: {
        enabled: false,
      },
      plotLines: [
        {
          value: 0,
          color: '#3F83F8',
          width: 3,
          zIndex: 2,
        },
      ],
    },
    {
      opposite: true,
      title: {
        text: 'Employee Count',
      },
      gridLineWidth: 0,
    },
  ],
  plotOptions: {
    column: {
      stacking: 'normal',
      borderWidth: 0,
      borderColor: 'transparent',
    },
  },
  legend: {
    itemStyle: {
      fontSize: '10px',
    },
    verticalAlign: 'bottom',
  },
  lang: {
    loading: 'Loading chart data...',
  },
};
